<script setup lang="ts">
import { computed } from "vue";

interface Props {
  color?: "default" | "default-darker" | "neutral" | "attention" | "warning";
  size?: "s" | "l";
}

const props = withDefaults(defineProps<Props>(), { color: "default", size: "s" });

const COMPONENT_CLASS = "le-label";

const componentClasses = computed(() => [
  COMPONENT_CLASS,
  `${COMPONENT_CLASS}--color-${props.color}`,
  `${COMPONENT_CLASS}--size-${props.size}`
]);
</script>

<template>
  <div :class="componentClasses">
    <slot />
  </div>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

@color-map: {
  @neutral: {
    border: 1px solid @colors[@primary][300];
    background: @colors[@primary][200];
    color: @colors[@primary][900];
  };
  @default: {
    border: 1px solid @colors[@secondary][500];
    background: @colors[@secondary][300];
    color: @colors[@neutrals][300];
  };
  @default-darker: {
    border: 1px solid @colors[@secondary][600];
    background: @colors[@secondary][400];
    color: @colors[@neutrals][400];
  };
  @attention: {
    border: 1px solid @colors[@accent][600];
    background: @colors[@accent][300];
    color: @colors[@accent][900];
  };
  @warning: {
    border: 1px solid @colors[@error][300];
    background: @colors[@error][200];
    color: @colors[@error][900];
  };
};

.le-label {
  display: inline-block;
  border-radius: 4px;

  &--size-s {
    padding: 2px 6px;
    .body-text("xs", @style: "italic");
  }

  &--size-l {
    padding: 4px 12px;
    .body-text("m", "semi-bold");
  }

  each(@color-map, {
    @color: replace(@key, "@", "");

    &--color-@{color} {
      background: @value[background];
      border:@value[border];
      color: @value[color];
    }
  });
}
</style>
